import { fetchAll } from 'config/fetchAll';
import React, { useEffect, useRef, useState } from 'react';
import AddNewPopUp, { Field } from 'reusable/AddEditDelete/AddNew';
import Export from 'reusable/export';
import { CsvCondition, csvDateTime } from 'config/csv';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import ErrorPopup from 'reusable/ErrorPopup';
import { deviceAssignmentMessages } from 'config/apiErrors';
import { useClassifiedErrorMessages } from 'reusable/useSaveError';
import { useTranslation } from 'react-i18next';
import { assignableDevices } from './deviceCandidates';
import { CleanupPendingError, OutcomeUnknownError, registerDriver } from './registerDriver';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onPageChange: (value: number) => void;
  driverData: any[];
  /** EV-142。表示している行の取得に成功した時点の条件。 */
  exportConditions?: CsvCondition[];
  /** ADM-14。途中で失敗したとき、一覧をサーバーの確定状態で取り直す。 */
  onChanged?: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  sortValue,
  onSearchChange,
  onSortChange,
  onPageChange,
  driverData,
  exportConditions,
  onChanged,
}) => {
  const { t } = useTranslation('driver');
  // ADM-15-AC1。通信断・403・5xx を区別した説明（以前は apiErrorMessages で区別しなかった）。
  const classifiedMessages = useClassifiedErrorMessages();
  const [device, setDevice] = useState<any[]>([]);
  const [errorPopupVisible, setErrorPopupVisible] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [popupTitle, setPopupTitle] = useState('');
  const deviceRequest = useRef(0);
  // EV-318。機器の候補を読めなかったとき（候補が無いことと区別して、機器の欄に出す）。
  const [deviceError, setDeviceError] = useState<unknown>(null);

  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.driver-id', { ns: 'common' }), value: 'id' },
  ];

  // Field key mapping to match formData fields with the expected API keys
  const fieldKeyMap: { [key: string]: string } = {
    [t('add-driver.driver-name')]: 'DRIVER NAME',
    [t('add-driver.email')]: 'EMAIL ADDRESS',
    [t('add-driver.dob')]: 'DATE OF BIRTH',
    [t('add-driver.assigned-device')]: 'ASSIGNED DEVICE',
    [t('add-driver.contact')]: 'CONTACT NUMBER',
  };

  useEffect(() => {
    fetchDeviceData();
  }, []);

  const fetchDeviceData = async () => {
    // 最初の読み込みと割当失敗後の読み直しが重なったとき、遅れた古い応答で候補を上書きしない。
    const request = ++deviceRequest.current;
    try {
      // BE-21。件数をpage_sizeに渡すとサーバー上限（100件）で切れる。
      const devices = await fetchAll<any>('/v1/devices/');
      if (request !== deviceRequest.current) return;
      setDevice(assignableDevices(devices));
      setDeviceError(null);
    } catch (error) {
      if (request !== deviceRequest.current) return;
      console.error('Failed to fetch data', error);
      // PR #253 の Codex の P2。読めなかったときは前回の候補を残さない（古い候補を選べないようにする）。
      setDevice([]);
      setDeviceError(error);
    }
  };

  const AddNewDriverAPI = async (formData: { [key: string]: any }) => {
    // Transform formData to use keys expected by the API
    const transformedData = Object.keys(formData).reduce((acc, label) => {
      const key = fieldKeyMap[label] || label; // Use mapped key or default to label
      acc[key] = formData[label];
      return acc;
    }, {} as { [key: string]: any });

    try {
      const payload: any = {
        name: transformedData['DRIVER NAME'],
        display_name: transformedData['DRIVER NAME'],
        birthday: transformedData['DATE OF BIRTH'],
        phone: transformedData['CONTACT NUMBER'],
        email: transformedData['EMAIL ADDRESS'],
        devices: transformedData['ASSIGNED DEVICE']?.length > 0
          ? transformedData['ASSIGNED DEVICE'].map((device: any) => device.id)
          : [],
      };

      // EV-023。機器の割当は devices に含め、サーバーが運転者の登録と1つの更新として
      // 確定する（割当の規則・台数の上限もそこで確かめる）。以前は登録の後に機器 API で
      // 同じ割当を1台ずつもう一度書き込み、その書き込みが失敗すると、実際には割り当て
      // 済みなのに「割当失敗」と表示していた。
      // EV-034。登録の操作ごとに X-Request-ID を付け、結果が分からない失敗では同じ値で
      // 送り直し、それでも分からなければ一覧をメールで照合する（registerDriver）。
      const outcome = await registerDriver(payload);
      // PR #99 レビュー。画面全体を再読み込みすると、保存を待つ間に開き直した追加画面の
      // 入力まで消える。一覧と未割当の機器だけをサーバーの確定状態で取り直す。
      onChanged?.();
      fetchDeviceData();
      if (outcome === 'created-without-invitation') {
        // EV-085。登録は残ったが、招待メールは届いていない。運転者への案内を促す。
        setPopupTitle(t('add-driver.notice'));
        setErrorMessages([t('add-driver.invitation-not-sent')]);
        setErrorPopupVisible(true);
      }
    } catch (error: any) {
      setPopupTitle(t('ui.error', { ns: 'common' }));
      if (error instanceof CleanupPendingError) {
        // EV-079。招待は届かず、取り消しが済んでいない登録が残っている。一覧はサーバーの
        // 今の状態で取り直し（残った登録が見える）、その理由と次の手順を示す。入力は残す。
        setErrorMessages([t('add-driver.cleanup-pending')]);
        onChanged?.();
      } else if (error instanceof OutcomeUnknownError) {
        // EV-034。登録できたかを確かめられなかった。入力は残し、一覧は取り直す
        // （後から登録が確定していれば一覧に出る）。
        setErrorMessages([t('add-driver.outcome-unknown')]);
        onChanged?.();
      } else {
        // 応答の値が文字列のとき join で落ちていた。code も表示していた。
        // BE-02。機器を割り当てられない理由（最上位の code）は画面の言語で出す。
        setErrorMessages(
          deviceAssignmentMessages(error, {
            inUse: t('ui.device-in-use'),
            notAvailable: t('ui.device-not-available'),
          }) ?? classifiedMessages(error)
        );
      }
      setErrorPopupVisible(true);
      console.error('Failed to post driver data', error);
      // ADM-15。運転者を作れなかったことを追加画面へ返し、閉じずに入力を残させる。
      throw error;
    }
  };

  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1); 
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    onPageChange(1); 
  };

  const exportFields = [
    { header: 'Driver ID', field: 'id' },
    { header: 'Driver Name', field: 'name' },
    { header: 'Exceed Limits', field: 'exceed' },
    { header: 'Contact Number', field: 'phone' },
    { header: 'Email Address', field: 'email' },
    { header: 'Registered Date', field: 'create_time', customExport: csvDateTime },
  ];

  const deviceOptions = device.map((device: any) => ({
    name: device.name,
    id: device.id,
  }));

  const title = `${t('add-driver.title')}`;
  const save = `${t('add-driver.add')}`;
  const fields: Field[] = [
    { type: 'text', label: `${t('add-driver.driver-name')}`, required: true },
    { type: 'email', label: `${t('add-driver.email')}`, required: true },
    { type: 'calendar', label: `${t('add-driver.dob')}`, required: true },
    {
      type: 'multi-select',
      label: `${t('add-driver.assigned-device')}`,
      options: deviceOptions,
      required: false,
      // EV-318。候補を読めなかったら、既存の文言（一覧を読み込めなかった・失敗の種類）と再読み込みを出す。
      note: deviceError
        ? [t('ui.fetch-failed', { ns: 'common' }), ...classifiedMessages(deviceError, '')]
            .filter(Boolean)
            .join(' ')
        : undefined,
      noteAction: deviceError
        ? { label: t('ui.retry', { ns: 'common' }), onClick: () => fetchDeviceData() }
        : undefined,
    },
    { type: 'text', label: `${t('add-driver.contact')}`, required: false },
  ];

  // 保存の完了（成否）を追加画面へ返す。
  const handleSave = (AddNewValues: { [key: string]: any }) =>
    AddNewDriverAPI(AddNewValues);

  return (
    <section className="flex flex-row justify-between w-full">
      <div className="flex w-2/5 justify-start space-x-2">
        <Search
          placeholder={t('table.search')}
          onSearchChange={handleSearchChange}
        />
      </div>
      <div className="flex flex-row space-x-2 items-center">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <AddNewPopUp
          label={title}
          title={title}
          fields={fields}
          save={save}
          onSave={handleSave}
        />
        <Export data={driverData} exportFields={exportFields} name="drivers" conditions={exportConditions} />
      </div>

      {/* Error Popup */}
      {errorPopupVisible && (
        <ErrorPopup
          title={popupTitle || t('ui.error', { ns: 'common' })}
          messages={errorMessages}
          onClose={() => setErrorPopupVisible(false)}
        />
      )}
    </section>
  );
};

export default Toolbar;
